import cx from 'classnames';
import Sticky from 'react-stickynode';
import { Media } from './media';
import { ErrorBoundary } from '../errorBoundary';
import React, { CSSProperties, ReactNode, FC, PropsWithChildren } from 'react';
import { useSiteConfig, useFeatures } from '../../scripts/selectors/global';
import { StickyEdgeAds } from '../ads/stickyEdgeAds';

type TTwoPaneContentProps = {
  className?: string;
  style?: CSSProperties;
  styleFirstElement?: CSSProperties;
  takeoverCollapse?: boolean;
  sticky?: boolean;
  allowCollapse?: boolean;
};

type TOnePaneContentProps = {
  className?: string;
  style?: CSSProperties;
};

type TEmbedPaneContentProps = {
  className?: string;
  style?: CSSProperties;
};

type TDynamicPaneContentProps = {
  className?: string;
  style?: CSSProperties;
  styleFirstElement?: CSSProperties;
  takeoverCollapse?: boolean;
  sticky?: boolean;
  allowCollapse?: boolean;
};

export const TwoPaneContent: FC<PropsWithChildren<TTwoPaneContentProps>> = (props) => {
  const siteConfig = useSiteConfig();
  const currentFeatures = useFeatures();

  const childrenArray = React.Children.toArray(props.children);
  const [firstChild, secondChild] = childrenArray;

  if (siteConfig.isEmbedded) {
    return <div className={props.className}>{firstChild}</div>;
  }

  return (
    <div id="content" className={cx('rio-content-pane', props.className)} style={props.style}>
      <StickyEdgeAds showRight={false} deniedPathPrefixes={DENIED_STICKY_PREFIXES} />

      <div className="slds-grid">
        <div
          className="slds-col slds-size--1-of-1"
          style={{
            ...props.styleFirstElement,
            minWidth: 0
          }}
        >
          <ErrorBoundary>{firstChild}</ErrorBoundary>
        </div>

        <Media greaterThanOrEqual="xl">
          {(mediaClassNames: string, renderChildren: boolean) => (
            <div
              className={cx(
                'slds-col slds-grow-none slds-shrink-none rio-right-pane',
                mediaClassNames,
                props.takeoverCollapse ? 'rio-takeover-hidden' : ''
              )}
            >
              {renderChildren && (
                <ErrorBoundary>
                  {props.sticky ? (
                    <Sticky
                      top={currentFeatures.authenticated ? '.login-bar' : 10}
                      bottomBoundary=".rio-content-pane"
                      className="rio-sidebar-section"
                    >
                      {secondChild}
                    </Sticky>
                  ) : (
                    secondChild
                  )}
                </ErrorBoundary>
              )}
            </div>
          )}
        </Media>
      </div>
    </div>
  );
};

const DENIED_STICKY_PREFIXES = ['/settings', '/tournaments', '/events'];

export const OnePaneContent: FC<PropsWithChildren<TOnePaneContentProps>> = (props) => {
  return (
    <div id="content" className={cx('rio-content-pane', props.className)} style={props.style}>
      <StickyEdgeAds showRight={false} deniedPathPrefixes={DENIED_STICKY_PREFIXES} />
      {props.children}
    </div>
  );
};

export const EmbedPaneContent: FC<PropsWithChildren<TEmbedPaneContentProps>> = (props) => {
  return (
    <div id="content" className={props.className} style={props.style}>
      {props.children}
    </div>
  );
};

export const DynamicPaneContent: FC<PropsWithChildren<TDynamicPaneContentProps>> = (props) => {
  const currentFeatures = useFeatures();

  if (currentFeatures.no_ads) {
    if (props.allowCollapse) {
      return <OnePaneContent {...props}>{props.children}</OnePaneContent>;
    } else {
      // FIXME: TODO, it felt weird to lose the extra column on many pages,
      // like: character M+ leaderboard, lowest keystone leaderboard, etc.
      return (
        <TwoPaneContent
          className={props.className}
          style={props.style}
          styleFirstElement={props.styleFirstElement}
          takeoverCollapse={props.takeoverCollapse}
          sticky={props.sticky}
          allowCollapse={props.allowCollapse}
          children={props.children as ReactNode[]}
        />
      );
    }
  } else {
    return (
      <TwoPaneContent
        className={props.className}
        style={props.style}
        styleFirstElement={props.styleFirstElement}
        takeoverCollapse={props.takeoverCollapse}
        sticky={props.sticky}
        allowCollapse={props.allowCollapse}
        children={props.children as ReactNode[]}
      />
    );
  }
};
